import { FC, useMemo, useEffect, useRef } from 'react';
import { useDropzone, FileRejection } from 'react-dropzone';
import { Upload, User, X } from 'lucide-react';
import { cn } from '@utils/cn';
import { Loading } from '@components/atoms/Loading';
import { SmallDocumentList } from '@components/molecules/SmallDocumentList';
import { Document } from '@models/document.model';
import { FileDropZoneProps, AcceptedFileTypes } from './FileDropZone.types';
import {
  DEFAULT_MAX_SIZE_BYTES,
  ACCEPTED_FILE_TYPES,
  COLOR_THEMES,
  DEFAULT_TEXTS,
} from './constants';

const resolveAcceptedTypes = (
  acceptedTypes: AcceptedFileTypes,
): Record<string, string[]> => {
  if (typeof acceptedTypes === 'string') {
    return ACCEPTED_FILE_TYPES[acceptedTypes] || ACCEPTED_FILE_TYPES.all;
  }
  return acceptedTypes;
};

export const FileDropZone: FC<FileDropZoneProps> = ({
  files,
  onFilesChange,
  multiple = true,
  maxFiles,
  maxSizeBytes = DEFAULT_MAX_SIZE_BYTES,
  acceptedTypes = 'all',
  disabled = false,
  isUploading = false,
  className,
  activeColor = 'indigo',
  title = DEFAULT_TEXTS.title,
  subtitle = DEFAULT_TEXTS.subtitle,
  dragActiveText = DEFAULT_TEXTS.dragActiveText,
  uploadingText = DEFAULT_TEXTS.uploadingText,
  showFileList = true,
  avatar = false,
  existingImageUrl,
  onFileRejected,
  onError,
}) => {
  const objectUrlsRef = useRef<string[]>([]);
  const avatarUrlRef = useRef<string | null>(null);
  const theme = COLOR_THEMES[activeColor];

  // Avatar preview URL - from selected file or existing URL
  const avatarPreviewUrl = useMemo(() => {
    if (avatar && files.length > 0) {
      // Clean up previous avatar URL
      if (avatarUrlRef.current) {
        URL.revokeObjectURL(avatarUrlRef.current);
      }
      const url = URL.createObjectURL(files[0]);
      avatarUrlRef.current = url;
      return url;
    }
    return existingImageUrl || null;
  }, [avatar, files, existingImageUrl]);

  // Cleanup avatar URL on unmount
  useEffect(() => {
    return () => {
      if (avatarUrlRef.current) {
        URL.revokeObjectURL(avatarUrlRef.current);
      }
    };
  }, []);

  const resolvedAccept = useMemo(
    () => resolveAcceptedTypes(acceptedTypes),
    [acceptedTypes],
  );

  const handleDrop = (acceptedFiles: File[]) => {
    if (maxFiles && files.length + acceptedFiles.length > maxFiles) {
      onError?.(`Maximum ${maxFiles} files allowed`);
      return;
    }
    onFilesChange([...files, ...acceptedFiles]);
  };

  const handleRejected = (rejections: FileRejection[]) => {
    rejections.forEach((rejection) => {
      const { file, errors } = rejection;
      const reason = errors.map((e) => e.message).join(', ');
      onFileRejected?.(file, reason);
    });
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: handleDrop,
    onDropRejected: handleRejected,
    multiple,
    accept: resolvedAccept,
    maxSize: maxSizeBytes,
    disabled: disabled || isUploading,
  });

  const removeFile = (index: number) => {
    const newFiles = files.filter((_, i) => i !== index);
    onFilesChange(newFiles);
  };

  const handleClearAvatar = (e: React.MouseEvent) => {
    e.stopPropagation();
    onFilesChange([]);
  };

  // Convert files to Document format for SmallDocumentList
  const documentsForDisplay: Document[] = useMemo(() => {
    // Clean up old object URLs
    objectUrlsRef.current.forEach((url) => {
      URL.revokeObjectURL(url);
    });
    objectUrlsRef.current = [];

    return files.map((file, index) => {
      const url = URL.createObjectURL(file);
      objectUrlsRef.current.push(url);
      return {
        documentId: `temp-${index}-${file.name}`,
        name: file.name,
        url,
        category: 'upload',
        createdAt: new Date().toISOString(),
      } as Document;
    });
  }, [files]);

  // Cleanup object URLs on unmount
  useEffect(() => {
    return () => {
      objectUrlsRef.current.forEach((url) => {
        URL.revokeObjectURL(url);
      });
    };
  }, []);

  const isDisabled = disabled || isUploading;

  const dropZoneClasses = cn(
    'border border-dashed rounded-lg p-4 text-center transition-all duration-200',
    isDisabled
      ? 'cursor-not-allowed opacity-50 border-gray-200 bg-gray-50 dark:bg-gray-800/50 dark:border-gray-700'
      : 'cursor-pointer',
    !isDisabled && isDragActive && [theme.border, theme.bg, theme.darkBg],
    !isDisabled &&
      !isDragActive && [
        'border-gray-200 bg-white',
        'dark:bg-gray-800/50 dark:border-gray-700',
        theme.hoverBorder,
        'hover:bg-gray-50 dark:hover:bg-gray-800',
      ],
  );

  const getDisplayText = (): string => {
    if (isUploading) return uploadingText;
    if (isDragActive) return dragActiveText;
    return title;
  };

  return (
    <div className={cn('space-y-3', className)}>
      {/* Avatar Preview */}
      {avatar && (
        <div className="flex justify-center mb-2">
          <div className="relative">
            <div
              {...getRootProps()}
              className={cn(
                'w-24 h-24 rounded-full overflow-hidden border-2 border-dashed cursor-pointer transition-all duration-200',
                isDisabled
                  ? 'cursor-not-allowed opacity-50 border-gray-200'
                  : 'hover:border-indigo-400',
                isDragActive && 'border-indigo-400 bg-indigo-50/50',
                !isDragActive && !isDisabled && 'border-gray-300',
              )}>
              <input {...getInputProps()} />
              {avatarPreviewUrl ? (
                <img
                  src={avatarPreviewUrl}
                  alt="Avatar preview"
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center bg-gray-100 dark:bg-gray-700">
                  <User className="w-10 h-10 text-gray-400 dark:text-gray-500" />
                </div>
              )}
            </div>
            {files.length > 0 && !isDisabled && (
              <button
                type="button"
                onClick={handleClearAvatar}
                className="absolute -top-1 -right-1 w-6 h-6 bg-red-500 hover:bg-red-600 text-white rounded-full flex items-center justify-center transition-colors shadow-sm">
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
      )}

      {/* Drop Zone */}
      <div
        {...getRootProps()}
        className={dropZoneClasses}
        role="button"
        aria-label="File upload dropzone">
        <input {...getInputProps()} />
        <div className="flex flex-col items-center gap-1">
          {isUploading ? (
            <Loading size="lg" className={cn('mb-1', theme.icon)} />
          ) : (
            <Upload
              className={cn(
                'w-8 h-8 mb-1',
                isDragActive ? theme.icon : 'text-gray-300 dark:text-gray-500',
              )}
            />
          )}
          <p className="text-sm text-gray-600 dark:text-gray-300">
            {getDisplayText()}
          </p>
          <p className="text-xs text-gray-400 dark:text-gray-500">{subtitle}</p>
          {maxFiles && !avatar && (
            <p className="text-xs text-gray-400 dark:text-gray-500">
              {files.length} / {maxFiles} files
            </p>
          )}
        </div>
      </div>

      {/* File List - hide in avatar mode */}
      {showFileList && !avatar && files.length > 0 && (
        <div>
          <SmallDocumentList
            documents={documentsForDisplay}
            showDelete={!isDisabled}
            onDelete={(document) => {
              const index = files.findIndex((f) => f.name === document.name);
              if (index !== -1) removeFile(index);
            }}
          />
        </div>
      )}
    </div>
  );
};

export default FileDropZone;
export type { FileDropZoneProps, AcceptedFileTypes } from './FileDropZone.types';
